// Activity heatmap — shows daily scored activity for the last year.
// Reads `activity_events` (standups, ticket updates, note/KB edits, meet
// calls). Intensity keyed on SUM(score) per day. Standup days keep a ✦
// marker in the tooltip so "did standup today" stays explicit even when
// bundled with other activity.

import { getSDK } from '../../../shared/functions/sdk.js'

const WEEKS = 52
const DAYS = 7
const TOTAL = WEEKS * DAYS

// Scored buckets: intensity ramps on summed points, not raw row count,
// so a single meet call (4pt) outshines two note edits (2pt).
const _intensity = (score, hasStandup) => {
  if (!score && !hasStandup) return 'transparent'
  if (hasStandup && !score) return 'green.15'
  if (score <= 2) return 'green.15'
  if (score <= 5) return 'green.3'
  if (score <= 10) return 'green.5'
  return 'green.7'
}

export const TicketsActivityHeatmap = {
  flow: 'y',
  gap: 'Z',

  CaptionTitle: {
    fontSize: 'Y2',
    Text: { text: 'ACTIVITY · LAST YEAR' }
  },

  Grid: {
    display: 'grid',
    gridTemplateColumns: `repeat(${WEEKS}, 1fr)`,
    gridTemplateRows: `repeat(${DAYS}, 1fr)`,
    gridAutoFlow: 'column',
    gap: 'X',
    padding: 'Z 0',

    childExtends: 'TicketsHeatmapCell',
    childrenAs: 'state',
    children: (el, s) => {
      const data = s.root?._activityData || {}
      const today = new Date()
      const cells = []
      for (let i = TOTAL - 1; i >= 0; i--) {
        const d = new Date(today)
        d.setDate(d.getDate() - i)
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        const entry = data[key] || {}
        cells.push({
          date: key,
          score: entry.score || 0,
          standups: entry.standups || 0,
          breakdown: entry.breakdown || {}
        })
      }
      return cells
    }
  },

  Legend: {
    flow: 'x',
    gap: 'Z',
    align: 'center right',
    fontSize: 'Y1',
    color: 'caption',

    Less: { tag: 'span', text: 'Less' },
    Box_1: { width: 'Y', height: 'Y', round: 'X', background: 'transparent', borderWidth: '1px', borderColor: 'line', borderStyle: 'solid' },
    Box_2: { width: 'Y', height: 'Y', round: 'X', background: 'green.15' },
    Box_3: { width: 'Y', height: 'Y', round: 'X', background: 'green.3' },
    Box_4: { width: 'Y', height: 'Y', round: 'X', background: 'green.5' },
    Box_5: { width: 'Y', height: 'Y', round: 'X', background: 'green.7' },
    More: { tag: 'span', text: 'More' }
  },

  DetailsCard: {
    hide: (el, s) => !s.root?._activitySelectedDate,
    flow: 'y',
    gap: 'Z1',
    marginTop: 'Z',
    padding: 'A',
    round: 'A',
    background: 'white.04',
    borderWidth: '1px',
    borderColor: 'line',
    borderStyle: 'solid',

    Header: {
      flow: 'x',
      align: 'center space-between',
      gap: 'Z',

      Title: {
        tag: 'span',
        fontSize: 'Z2',
        fontWeight: '700',
        color: 'title',
        text: (el, s) => {
          const date = s.root?._activitySelectedDate
          if (!date) return ''
          const d = new Date(date + 'T00:00:00')
          return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })
        }
      },

      CloseBtn: {
        tag: 'button',
        type: 'button',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: 'caption',
        fontSize: 'A',
        padding: 'V Z',
        text: '×',
        ':hover': { color: 'title' },
        onClick: (e, el, s) => s.root?.update({ _activitySelectedDate: null, _activityDetails: null })
      }
    },

    Loading: {
      hide: (el, s) => !s.root?._activitySelectedDate || !!s.root?._activityDetails,
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: 'Loading…'
    },

    Empty: {
      hide: (el, s) => {
        const list = s.root?._activityDetails
        return !Array.isArray(list) || list.length > 0
      },
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      fontStyle: 'italic',
      text: 'No activity recorded for this day.'
    },

    List: {
      hide: (el, s) => !(s.root?._activityDetails || []).length,
      flow: 'y',
      gap: 'V',
      childExtends: 'TicketsActivityRow',
      childrenAs: 'state',
      children: (el, s) => s.root?._activityDetails || []
    }
  },

  // Fetch real activity data — ticket updates + standup completions
  onCreate: async (el, s) => {
    const root = s.root
    if (!root) return
    let email = root.currentUserEmail
    if (!email) {
      await new Promise(resolve => {
        let attempts = 0
        const check = setInterval(() => {
          email = root.currentUserEmail
          if (email || ++attempts > 30) { clearInterval(check); resolve() }
        }, 200)
      })
    }
    if (!email) return

    try {
      // migrated 2026-04-27 to SDK route workspaceProject.activity heatmap
      const since = new Date()
      since.setDate(since.getDate() - 365)
      const sinceDate = since.toISOString().slice(0, 10)

      // Single RPC — wrapper aggregates every activity type and scopes
      // by caller's user_email + workspace_id from the JWT.
      const result = await getSDK().execute('workspaceProject.activity', 'heatmap', {
        filter: {
          user_email: email,
          since: sinceDate,
          select: 'activity_type, score, occurred_on'
        }
      })
      const data = Array.isArray(result?.data) ? result.data : (result?.data || result || [])

      const counts = {}
      for (const row of (data || [])) {
        const day = row.occurred_on
        if (!day) continue
        if (!counts[day]) counts[day] = { score: 0, standups: 0, breakdown: {} }
        counts[day].score += row.score || 0
        counts[day].breakdown[row.activity_type] =
          (counts[day].breakdown[row.activity_type] || 0) + 1
        if (row.activity_type === 'standup') counts[day].standups++
      }

      root.update({ _activityData: counts })
    } catch (err) {
      console.error('[ActivityHeatmap]', err)
    }
  }
}

const _typeLabel = {
  standup: 'standup',
  ticket_update: 'ticket update',
  note_edit: 'note edit',
  kb_edit: 'KB edit',
  meet_call: 'meet call'
}

const _loadDetails = async (root, date) => {
  root.update({ _activitySelectedDate: date, _activityDetails: null })
  try {
    // migrated 2026-04-27 to SDK route workspaceProject.activity listEvents
    const result = await getSDK().execute('workspaceProject.activity', 'listEvents', {
      filter: {
        user_email: root.currentUserEmail,
        occurred_on: date,
        select: 'activity_type, score, source_table, source_id, metadata, created_at'
      },
      order: { column: 'created_at', ascending: true }
    })
    const data = Array.isArray(result?.data) ? result.data : (result?.data || result || [])
    root.update({ _activityDetails: data || [] })
  } catch (err) {
    console.error('[ActivityHeatmap details]', err)
    root.update({ _activityDetails: [] })
  }
}

export const TicketsHeatmapCell = {
  width: '100%',
  aspectRatio: '1',
  round: 'X',
  background: (el, s) => _intensity(s.score, s.standups > 0),
  borderWidth: (el, s) => (s.score || s.standups) ? '0' : '1px',
  borderColor: 'line',
  borderStyle: 'solid',
  cursor: (el, s) => (s.score || s.standups) ? 'pointer' : 'default',
  transition: 'background 120ms ease',
  ':hover': { outline: '1px solid var(--color-title)' },
  title: (el, s) => {
    if (!s.score && !s.standups) return s.date
    const parts = []
    if (s.standups) parts.push('✦ standup')
    const bd = s.breakdown || {}
    for (const [k, n] of Object.entries(bd)) {
      if (k === 'standup') continue
      parts.push(`${n} ${_typeLabel[k] || k}${n > 1 ? 's' : ''}`)
    }
    return `${s.date} · ${s.score}pt${s.score === 1 ? '' : 's'}${parts.length ? ' — ' + parts.join(', ') : ''}`
  },
  onClick: (e, el, s) => {
    if (!s.score && !s.standups) return
    _loadDetails(s.root, s.date)
  }
}

const _typeColor = (t) =>
  t === 'standup' ? 'green' : t === 'meet_call' ? 'purple' : t === 'kb_edit' || t === 'note_edit' ? 'cerulean' : 'blue'

const _hhmmss = (ts) => {
  try {
    return new Date(ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
  } catch { return '' }
}

const _detailFor = (s) => {
  const md = s.metadata || {}
  return md.title || md.summary || md.body || (s.source_id ? `${s.source_table || 'item'}·${s.source_id}` : '')
}

export const TicketsActivityRow = {
  flow: 'x',
  gap: 'Z',
  align: 'baseline left',
  padding: 'X Z',
  fontFamily: 'mono',
  fontSize: 'Y2',
  lineHeight: '1.5',
  ':hover': { background: 'overlay' },

  Time: {
    tag: 'span',
    color: 'caption',
    flexShrink: '0',
    text: (el, s) => _hhmmss(s.created_at)
  },

  Type: {
    tag: 'span',
    flexShrink: '0',
    minWidth: 'C',
    fontWeight: '700',
    color: (el, s) => _typeColor(s.activity_type),
    text: (el, s) => (_typeLabel[s.activity_type] || s.activity_type || '').toUpperCase()
  },

  Detail: {
    tag: 'span',
    flex: '1',
    color: 'title',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => _detailFor(s)
  }
}
